import Phaser from 'phaser';
import { centerMowerIcon } from '../ui/mowerIcon';
import { BIOMES, type BiomeDef, type BiomeId } from '../../content/biomes';
import { allowedMowers, type LevelDef } from '../../content/levelSchema';
import { CAMPAIGN_LEVELS } from '../../content/levels/campaign';
import { PROTO_LEVELS } from '../../content/levels/proto';
import { MOWERS, MOWER_IDS, type MowerId } from '../../content/mowers';
import {
  dailyLawn,
  ENDLESS_MODES,
  endlessLawn,
  endlessModeAllows,
  endlessStage,
  type DailyLawn,
  type EndlessMode,
} from '../../gen/modes';
import {
  campaignLevelId,
  campaignProgress,
  cashOutOpenRun,
  dailyReward,
  isCampaignLevelOpen,
  ownedMowerIds,
} from '../../meta/economy';
import { STAR, STAR_BITS } from '../../meta/save/schema';
import { TICK_RATE } from '../../sim/constants';
import { Dir } from '../../sim/direction';
import { dailyBestTicks, endlessBest, newRunSeed, saves, todayKey } from '../records';
import { needsFullGame } from '../../meta/unlock';
import {
  bevelButton,
  callAttention,
  clearScene,
  drawBigStar,
  lawnThumbnail,
  navyPanel,
  thumbnailScale,
} from '../ui/chrome';
import { drawCheck, drawCoin, drawPadlock, formatTime } from '../ui/widgets';
import { onAppShown } from '../../platform/native';
import { claimAwards, featuredNow, loadFeatured, onlineOn } from '../online';
import { awardMessage } from '../awardMessage';
import type { BoardsData } from './BoardsScene';
import type { ChallengeData } from './ChallengeScene';
import { pendingChallengeId, pendingFriendId, pendingRaceCode } from '../links';
import type { RaceData } from './RaceScene';
import { button, label, MessagePanel, UI } from '../ui/text';
import type { LevelCardData } from './LevelCardScene';
import type { LevelSceneData, Playlist } from './LevelScene';
import { wipeTo } from './WipeScene';
import { arriveSince, reducedMotion } from '../ui/motion';
import { BUILDER_SHOP, builderOpen, builderPrice } from '../../meta/customLawns';
import { openLawnCode, pendingLawnCode } from './sharedLawn';
import { bossFor, BOSS_LEVELS } from '../../gen/boss';
import { ownMowerTexture } from '../render/cosmeticArt';
import { devAreaOpen, REG_MENU_MODE } from '../devAccess';
import { askForNotificationsOnce } from '../notifyAsk';
import { drawBossBadge, drawNeighborhood, drawWorldIcon } from '../ui/zoneArt';
import {
  drawHeader,
  drawTabBar,
  HEADER_H,
  REG_MENU_HOME,
  TABBAR_H,
  type NavTab,
} from '../ui/navChrome';

type Mode = 'campaign' | 'daily' | 'endless' | 'test';

/** The All-Stars trophy's gold. */
const GOLD = 0xffd23f;
const REG_MODE = REG_MENU_MODE;
const REG_DAILY = 'menu.daily';
const REG_PAGE = 'menu.campaignPage';
/** The campaign lawn shown in the bottom card (index into CAMPAIGN_LEVELS). */
const REG_SELECTED = 'menu.selected';
/** Campaign progress when the picker last drew: a change (a new lawn cleared) resets the pick. */
const REG_PROGRESS = 'menu.progressSeen';
/** Shared with the level card: the mower last picked there. */
const REG_MOWER_CHOICE = 'card.mower';

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

/** The header's name for each face of the menu. */
const MODE_TITLE: Record<Mode, string> = {
  campaign: 'CAMPAIGN',
  daily: 'DAILY LAWN',
  endless: 'ENDLESS RUNS',
  test: 'DEV LAWNS',
};

/** Outside-the-lawn ground (docs/ART_DIRECTION.md); menus sit on the world. */
const BACKGROUND = '#2b4a26';
const BANNER_H = 42;
/** The banner on short (4:3) screens: just the zone plate, so four rows of plates still fit. */
const BANNER_H_COMPACT = 32;
/** Shortest plate that still holds its number and stars (small numbers, on short screens). */
const MIN_PLATE_H = 19;
/** Plates this tall carry the big (2x) numbers; shorter ones go small, five to a row. */
const BIG_PLATE_H = 30;
const CARD_H = 64;
const ROW = 12;
const COLS = 5;
/** The strip of world icons under the zone banner. */
const STRIP_H = 12;
/** A sideways drag across the lawn grid this long (game px) turns the zone page. */
const SWIPE_PAGE = 24;

/**
 * Main menu (docs/ART_DIRECTION.md, level picker): mode tabs, then for the campaign a zone plate
 * over its neighborhood, a grid of yard plates (big numbers, bold stars), and the selected lawn
 * card with MOW! at the bottom in thumb reach; Garage, Shop and Settings in the nav bar.
 */
export class MenuScene extends Phaser.Scene {
  /** Set by a plate tap: the next draw points at MOW! (see callAttention). */
  private nudgeMow = false;
  /**
   * How the next draw arrives: the scene opening (or a tab switch), a page turn, or nothing (a
   * redraw in place, e.g. selecting a plate, which must not replay the entrance).
   */
  private entrance: 'enter' | 'prev' | 'next' | null = 'enter';
  /** Filled stars on the page, for the occasional twinkle. */
  private twinkleSpots: { x: number; y: number }[] = [];

  constructor() {
    super('Menu');
  }

  create(): void {
    this.cameras.main.setBackgroundColor(BACKGROUND);
    this.entrance = 'enter';
    this.draw();
    // Back on the Daily tab after midnight (owner, 2026-10-31): today's lawn, not yesterday's.
    const stop = onAppShown(() => {
      if (this.registry.get(REG_MODE) === 'daily' && this.dailyShown !== todayKey()) this.redraw();
    });
    this.events.once('shutdown', stop);
    this.payOpenRun();
    // A shared lawn's link: straight to its card (or My Lawns, saying what was wrong with it).
    const shared = pendingLawnCode();
    if (shared) {
      this.time.delayedCall(250, () => {
        const problem = openLawnCode(this, shared);
        if (problem) wipeTo(this, 'MyLawns', { message: problem });
      });
    }
    // A race's link: its room (asking first).
    const race = pendingRaceCode();
    if (race) {
      this.time.delayedCall(250, () =>
        wipeTo(this, 'Race', {
          join: race.code,
          ...(race.joined ? { accepted: true } : {}),
        } satisfies RaceData),
      );
    }
    // A challenge's link: its card.
    const challenge = pendingChallengeId();
    if (challenge) {
      this.time.delayedCall(250, () =>
        wipeTo(this, 'Challenge', { id: challenge } satisfies ChallengeData),
      );
    }
    // A friend's link: asks to add them.
    const friend = pendingFriendId();
    if (friend) {
      this.time.delayedCall(250, () => wipeTo(this, 'Friends', { add: friend }));
    }
    // After the first lawn: may the game send notifications? (Asked once; not over a link's screen.)
    if (!shared && !race && !challenge && !friend) {
      this.time.delayedCall(600, () => {
        if (this.scene.isActive()) void askForNotificationsOnce();
      });
    }
    // Online: the week's featured lawn (for the Daily tab), and any holding bonuses to pay.
    if (onlineOn()) {
      if (!featuredNow()) {
        void loadFeatured().then((f) => {
          if (f && this.scene.isActive() && this.registry.get(REG_MODE) === 'daily') this.redraw();
        });
      }
      void claimAwards().then((payout) => {
        if (!payout || !this.scene.isActive()) return;
        // The header's wallet shows the coins; the note goes over the redrawn menu.
        this.redraw();
        this.showNote(awardMessage(payout));
      });
    }
    this.scale.on(Phaser.Scale.Events.RESIZE, this.redraw);
    // Swipe sideways across the lawn grid to change zones.
    const onUp = (p: Phaser.Input.Pointer) => {
      const area = this.swipeArea;
      if (p.wasCanceled || !area || p.downY < area.top || p.downY > area.bottom) return;
      const dx = p.upX - p.downX;
      const dy = p.upY - p.downY;
      if (Math.abs(dx) < SWIPE_PAGE || Math.abs(dx) < Math.abs(dy) * 1.5) return;
      area.turn(dx < 0 ? 1 : -1);
    };
    this.input.on('pointerup', onUp);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.scale.off(Phaser.Scale.Events.RESIZE, this.redraw);
      this.input.off('pointerup', onUp);
    });
  }

  /**
   * An endless run the app closed on (it is kept in the save lawn by lawn) pays out now, as if it
   * had ended there, with a note saying so. Tap or wait to dismiss.
   */
  private payOpenRun(): void {
    let paid: number | null = null;
    if (!saves().save.endless.openRun) return;
    saves().update((s) => (paid = cashOutOpenRun(s)));
    if (paid === null) return;
    const coins = paid as number;
    this.showNote(
      `Your last endless run was cut short.\n\nIt paid out ${coins} coin${coins === 1 ? '' : 's'}.`,
    );
  }

  /** A note over the menu, in the middle of the screen. Tap or wait to dismiss. */
  private showNote(text: string): void {
    const { width, height } = this.scale.gameSize;
    const panel = new MessagePanel(this, width - 24)
      .setMessage(text, width / 2, height / 2)
      .setDepth(50);
    // A screen-wide catcher over the menu: the dismissing tap presses nothing underneath.
    const catcher = this.add
      .zone(0, 0, width, height)
      .setOrigin(0)
      .setDepth(49)
      .setInteractive()
      .on('pointerup', () => hide());
    const hide = () => {
      panel.container.destroy();
      catcher.destroy();
    };
    this.time.delayedCall(4000, () => {
      if (catcher.active) hide();
    });
  }

  /** The campaign lawn grid (for zone swipes) and how to turn the page by one. */
  private swipeArea: { top: number; bottom: number; turn: (by: number) => void } | null = null;

  /** The day of the Daily tab on screen (to draw it again once the day turns). */
  private dailyShown: string | null = null;

  private readonly redraw = (): void => {
    this.tweens.killAll();
    this.time.removeAllEvents();
    clearScene(this);
    this.swipeArea = null;
    this.draw();
  };

  private draw(): void {
    const { height } = this.scale.gameSize;
    const picked = (this.registry.get(REG_MODE) as Mode | undefined) ?? 'campaign';
    const mode = picked === 'test' && !devAreaOpen() ? 'campaign' : picked;
    const bottom = height - TABBAR_H;
    const top = HEADER_H + 2;
    const tab: NavTab = mode === 'daily' ? 'daily' : mode === 'endless' ? 'runs' : 'lawns';
    drawHeader(this, MODE_TITLE[mode], tab);
    switch (mode) {
      case 'campaign':
        this.drawCampaign(top, bottom);
        break;
      case 'daily':
        this.drawDaily(top + 6, bottom);
        break;
      case 'endless':
        this.drawEndless(top + 4, bottom);
        break;
      case 'test':
        this.drawTests(top + 4, bottom);
        break;
    }
    drawTabBar(this, tab);
    this.startTwinkles();
    this.entrance = null;
  }

  // ── Motion (docs/ART_DIRECTION.md: menus with life) ───────────────────────────────────────

  private get still(): boolean {
    return reducedMotion();
  }

  /** Now and then a filled star on the page catches the light. */
  private startTwinkles(): void {
    const spots = this.twinkleSpots;
    this.twinkleSpots = [];
    if (this.still || spots.length === 0) return;
    this.time.addEvent({
      delay: 1300,
      loop: true,
      callback: () => {
        const s = spots[Math.floor(Math.random() * spots.length)]!;
        const g = this.add.graphics().setPosition(s.x + 3, s.y + 3);
        g.fillStyle(0xffffff).fillRect(0, -3, 1, 7).fillRect(-3, 0, 7, 1).fillRect(-1, -1, 3, 3);
        this.tweens.add({
          targets: g,
          alpha: { from: 1, to: 0 },
          duration: 500,
          ease: 'Quad.easeIn',
          onComplete: () => g.destroy(),
        });
      },
    });
  }

  // ── Chrome ────────────────────────────────────────────────────────────────────────────────

  // ── Campaign ──────────────────────────────────────────────────────────────────────────────

  private drawCampaign(top: number, bottom: number): void {
    const { width } = this.scale.gameSize;
    const save = saves().save;
    const levels = CAMPAIGN_LEVELS;

    // Zone: one page per biome; opens on the biome of the next lawn to play.
    const pages = BIOMES.filter((b) => b.levels[0] <= levels.length);
    // A lawn cleared since the picker last drew: back to the next lawn (and its page), so MOW!
    // never quietly replays the one just finished.
    const progress = campaignProgress(save);
    // (LAWNS tapped on the lawns: home to the next lawn's zone too.)
    const home = this.registry.get(REG_MENU_HOME) === true;
    this.registry.remove(REG_MENU_HOME);
    if (home || this.registry.get(REG_PROGRESS) !== progress) {
      this.registry.set(REG_PROGRESS, progress);
      this.registry.remove(REG_SELECTED);
      this.registry.remove(REG_PAGE);
    }
    const next = Math.min(campaignProgress(save) + 1, levels.length);
    const pageOf = (n: number) => pages.findIndex((b) => n >= b.levels[0] && n <= b.levels[1]);
    const page = clamp(
      (this.registry.get(REG_PAGE) as number | undefined) ?? Math.max(0, pageOf(next)),
      0,
      pages.length - 1,
    );
    const biome = pages[page] as BiomeDef;
    const first = biome.levels[0];
    const last = Math.min(biome.levels[1], levels.length);
    const selected = this.selectedLevel(first, last, next);

    // Three-digit lawn numbers need wider plates: four to a row, unless the screen is too short
    // for the extra row (then small numbers, five to a row).
    const count = last - first + 1;
    const gap = 3;
    const cardTop = bottom - CARD_H - 4;
    const plateRoom = (banner: number, cols: number) =>
      Math.floor((cardTop - (top + banner + 4) - 3) / Math.ceil(count / cols)) - gap;
    const wide = String(last).length >= 3;
    const cols =
      wide && plateRoom(BANNER_H_COMPACT + STRIP_H, COLS - 1) >= BIG_PLATE_H ? COLS - 1 : COLS;
    // Room check: on short screens the banner gives up its houses so every plate fits.
    const bannerH =
      (plateRoom(BANNER_H + STRIP_H, cols) >= 24 ? BANNER_H : BANNER_H_COMPACT) + STRIP_H;

    // Zones seen: a first look at a newly opened zone gets a banner; the way there pulses until
    // then. (A save from before this starts out having seen everything up to here.)
    const unlockedPage = (i: number) => isCampaignLevelOpen(save, pages[i]!.levels[0]);
    if (save.zonesVisited.length === 0) {
      saves().update((s) => (s.zonesVisited = pages.slice(0, page + 1).map((b) => b.id)));
    }
    const firstLook = !saves().save.zonesVisited.includes(biome.id) && unlockedPage(page);
    if (firstLook) saves().update((s) => s.zonesVisited.push(biome.id));
    const unseen = (i: number) =>
      unlockedPage(i) && !saves().save.zonesVisited.includes(pages[i]!.id);

    // Zone plate over the neighborhood.
    drawNeighborhood(this, top, bannerH - STRIP_H, biome.id);
    const zoneMark = this.children.length;
    const g = this.add.graphics();
    const plateW = width - 40;
    navyPanel(g, 20, top + 4, plateW, 26);
    const turn = (to: number) => {
      this.registry.set(REG_PAGE, to);
      this.registry.remove(REG_SELECTED);
      this.entrance = to > page ? 'next' : 'prev';
      this.redraw();
    };
    if (page > 0) {
      const prev = button(this, 26, top + 8, '<', () => turn(page - 1), 1, UI.text);
      if (pages.slice(0, page).some((_, i) => unseen(i))) this.pulse(prev);
    }
    if (page < pages.length - 1) {
      const next = button(this, 20 + plateW - 6, top + 8, '>', () => turn(page + 1), 1, UI.text);
      next.setOrigin(1, 0);
      if (pages.some((_, i) => i > page && unseen(i))) this.pulse(next);
    }
    this.swipeArea = {
      top: top + bannerH,
      bottom: cardTop,
      turn: (by: number) => {
        const to = page + by;
        if (to >= 0 && to < pages.length) turn(to);
      },
    };
    if (firstLook && page > 0) this.newZoneBanner(biome.name);
    label(this, width / 2, top + 8, biome.name.toUpperCase(), 1, UI.accent).setOrigin(0.5, 0);
    let zoneStars = 0;
    let zoneDone = 0;
    for (let n = first; n <= last; n++) {
      const stars = save.levels[levels[n - 1]!.id]?.stars ?? 0;
      zoneStars += STAR_BITS.filter((b) => stars & b).length;
      if (stars & STAR.clear) zoneDone++;
    }
    // Every star in the zone: its trophy, the totals in gold.
    const allStars = zoneStars === (last - first + 1) * 3;
    const totals = allStars
      ? `ALL STARS ${zoneStars}/${zoneStars}`
      : `${zoneStars}/${(last - first + 1) * 3}   ${zoneDone}/${last - first + 1}`;
    const totalsText = label(
      this,
      width / 2 + 5,
      top + 18,
      totals,
      1,
      allStars ? GOLD : UI.text,
    ).setOrigin(0.5, 0);
    drawBigStar(g, Math.round(totalsText.x - totalsText.width / 2 - 10), top + 18, true);
    const side = this.entrance === 'next' ? 1 : this.entrance === 'prev' ? -1 : 0;
    if (this.entrance === 'enter') arriveSince(this, zoneMark, 0, -12, 0);
    else if (side !== 0) arriveSince(this, zoneMark, side * 50, 0, 0, 220, 'Quad.easeOut');
    // After the banner's entrance (which fades in what it covers): locked worlds stay dimmed.
    this.drawWorldStrip(top + bannerH - STRIP_H, page, pages.length, unseen, turn);

    // Yard plates, sized to the room between the banner and the card.
    const gridTop = top + bannerH + 4;
    const cellW = Math.floor((width - 10 - gap * (cols - 1)) / cols);
    const cellH = clamp(plateRoom(bannerH, cols), MIN_PLATE_H, 44);
    const x0 = Math.round((width - (cols * cellW + (cols - 1) * gap)) / 2);
    for (let n = first; n <= last; n++) {
      const j = n - first;
      const mark = this.children.length;
      this.drawPlate(
        x0 + (j % cols) * (cellW + gap),
        gridTop + Math.floor(j / cols) * (cellH + gap),
        cellW,
        cellH,
        n,
        next,
        n === selected,
      );
      // Plates drop in one after another; on a page turn they slide in from that side.
      const col = j % cols;
      const row = Math.floor(j / cols);
      if (this.entrance === 'enter') arriveSince(this, mark, 0, -8, 60 + j * 18);
      else if (side !== 0) {
        const order = side > 0 ? col : cols - 1 - col;
        arriveSince(this, mark, side * 40, 0, order * 28 + row * 10, 220, 'Quad.easeOut');
      }
    }

    const cardMark = this.children.length;
    this.drawLawnCard(cardTop, selected, next);
    if (this.entrance === 'enter') arriveSince(this, cardMark, 0, 24, 120, 320);
  }

  /**
   * A strip of world icons under the zone banner: every world in the game, the current one
   * framed, the ones still to build as padlocks, a newly opened one pulsing. Tap to go there.
   */
  private drawWorldStrip(
    y: number,
    page: number,
    built: number,
    unseen: (i: number) => boolean,
    turn: (to: number) => void,
  ): void {
    const { width } = this.scale.gameSize;
    const g = this.add.graphics();
    g.fillStyle(UI.shadow).fillRect(0, y, width, STRIP_H);
    const step = 15;
    const x0 = Math.round(width / 2 - ((BIOMES.length - 1) * step) / 2) - 4;
    BIOMES.forEach((b, i) => {
      const x = x0 + i * step;
      const iy = y + 2;
      if (i === page) {
        g.fillStyle(UI.panel).fillRect(x - 2, y + 1, 13, STRIP_H - 2);
        g.lineStyle(1, UI.accent).strokeRect(x - 1.5, y + 1.5, 12, STRIP_H - 3);
      }
      const icon = this.add.graphics();
      if (i < built) drawWorldIcon(icon, b.id, x, iy);
      else drawPadlock(icon, x, iy);
      if (i < built && i !== page && !isCampaignLevelOpen(saves().save, b.levels[0]))
        icon.setAlpha(0.45);
      if (i < built && unseen(i) && i !== page) this.pulse(icon);
      if (i < built && i !== page) {
        this.add
          .zone(x - 3, y, step, STRIP_H)
          .setOrigin(0)
          .setData('label', b.name)
          .setInteractive({ useHandCursor: true })
          .on('pointerup', () => turn(i));
      }
    });
  }

  /** A gentle, endless pulse drawing the eye to something new (off with reduced motion). */
  private pulse(target: Phaser.GameObjects.GameObject): void {
    if (this.still) return;
    this.tweens.add({ targets: target, alpha: 0.35, duration: 500, yoyo: true, repeat: -1 });
  }

  /** First look at a newly opened zone: a NEW ZONE banner sweeps across and away. */
  private newZoneBanner(name: string): void {
    const { width, height } = this.scale.gameSize;
    const g = this.add.graphics();
    const h = 34;
    const y = Math.round(height / 2 - h / 2 - 40);
    g.fillStyle(UI.shadow).fillRect(0, y + 2, width, h);
    g.fillStyle(UI.panel).fillRect(0, y, width, h);
    g.fillStyle(UI.accent)
      .fillRect(0, y, width, 1)
      .fillRect(0, y + h - 1, width, 1);
    const top = label(this, width / 2, y + 5, 'NEW ZONE', 1, UI.accent).setOrigin(0.5, 0);
    // Big where it fits (the font is 6 px a letter at 1x).
    const scale = name.length * 12 <= width - 8 ? 2 : 1;
    const big = label(
      this,
      width / 2,
      y + (scale === 2 ? 15 : 19),
      name.toUpperCase(),
      scale,
      UI.text,
    ).setOrigin(0.5, 0);
    const parts = [g, top, big];
    for (const p of parts) p.setDepth(80);
    if (this.still) {
      this.time.delayedCall(1800, () => parts.forEach((p) => p.destroy()));
      return;
    }
    for (const p of parts) {
      const x = (p as unknown as { x: number }).x;
      (p as unknown as { x: number }).x = x + width;
      this.tweens.add({
        targets: p,
        x,
        duration: 380,
        ease: 'Back.easeOut',
        onComplete: () =>
          this.tweens.add({
            targets: p,
            x: x - width,
            delay: 1500,
            duration: 300,
            ease: 'Quad.easeIn',
            onComplete: () => p.destroy(),
          }),
      });
    }
  }

  /** The lawn in the bottom card: the stored pick if it is on this page, else the page's next. */
  private selectedLevel(first: number, last: number, next: number): number {
    const save = saves().save;
    const stored = this.registry.get(REG_SELECTED) as number | undefined;
    if (stored !== undefined && stored >= first && stored <= last) return stored;
    if (next >= first && next <= last) return next;
    return isCampaignLevelOpen(save, last) ? last : first;
  }

  private drawPlate(
    x: number,
    y: number,
    w: number,
    h: number,
    n: number,
    next: number,
    selected: boolean,
  ): void {
    const save = saves().save;
    const level = CAMPAIGN_LEVELS[n - 1]!;
    const stars = save.levels[level.id]?.stars ?? 0;
    const done = (stars & STAR.clear) !== 0;
    const open = isCampaignLevelOpen(save, n);
    // Open by progress, but past the free lawns: the full game's gold padlock.
    const paid = open && needsFullGame(save, n);
    const isNext = n === next && !done;
    const g = this.add.graphics();

    // The yard behind (world material): mowed when done, bright when next, overgrown when locked.
    if (done) {
      for (let k = 0; k < w; k += 4) {
        g.fillStyle(k % 8 === 0 ? 0x27702e : 0x1d5a25).fillRect(x + k, y, Math.min(4, w - k), h);
      }
    } else {
      g.fillStyle(open ? 0x3f9a35 : 0x234d22).fillRect(x, y, w, h);
      g.fillStyle(open ? 0xd2f58a : 0x4f7a43);
      for (let ty = y + 3; ty < y + h - 2; ty += 5) {
        for (let tx = x + 2 + ((ty - y) % 10 === 3 ? 0 : 2); tx < x + w - 2; tx += 5) {
          g.fillRect(tx, ty, 1, 2);
          g.fillRect(tx + 2, ty, 1, 2);
        }
      }
    }
    g.lineStyle(1, open ? 0xffffff : 0x10180c).strokeRect(x + 0.5, y + 0.5, w - 1, h - 1);
    if (selected) {
      // 2 px of orange just outside the plate, then an ink line, all within the 3 px gap.
      g.lineStyle(2, UI.select).strokeRect(x - 1, y - 1, w + 2, h + 2);
      g.lineStyle(1, UI.shadow).strokeRect(x - 2.5, y - 2.5, w + 5, h + 5);
    }

    // Number plate (UI material) and stars. Short plates (short screens) take small numbers.
    const num = String(n);
    // (Big numbers need the height, and the width: three digits are too wide five to a row.)
    const big = h >= BIG_PLATE_H && num.length * 12 + 8 <= w;
    const boss = bossFor(n);
    const k = big ? 2 : 1;
    const plateW = big ? num.length * 12 + 6 : num.length * 6 + 5;
    // (A short plate's boss badge takes its top right corner: the number moves over.)
    const px = x + (!big && boss ? 2 : Math.round((w - plateW) / 2));
    const plateH = big ? 16 : 9;
    g.fillStyle(UI.shadow).fillRect(px - 1, y + (big ? 3 : 2), plateW + 2, plateH + 2);
    g.fillStyle(open ? UI.panel : 0x2a2550).fillRect(px, y + (big ? 4 : 3), plateW, plateH);
    label(
      this,
      px + 2 + k,
      y + (big ? 5 : 4),
      num,
      k,
      isNext ? UI.accent : open ? UI.text : 0x6f68a0,
    );
    const starY = big ? y + Math.max(22, h - 11) : y + Math.max(12, h - 8);
    if (done) {
      STAR_BITS.forEach((bit, k) => {
        const sx = x + Math.round(w / 2) - 13 + k * 9;
        drawBigStar(g, sx, starY, (stars & bit) !== 0);
        if (stars & bit) this.twinkleSpots.push({ x: sx, y: starY });
      });
    }
    // Zone bosses carry a badge: between the number and the stars, or (short plates) on the
    // plate's corner like a sticker.
    if (boss && big && starY - (y + 23) >= 10) {
      drawBossBadge(g, boss, x + Math.round(w / 2) - 4, y + 23);
    } else if (boss && big) drawBossBadge(g, boss, x + w - 7, y + h - 7);
    else if (boss) drawBossBadge(g, boss, x + w - 11, y + 2);
    if (paid && h >= 34) {
      g.fillStyle(0x10180c).fillRect(x + w / 2 - 4, starY + 1, 9, 7);
      g.fillStyle(UI.accent).fillRect(x + w / 2 - 3, starY + 2, 7, 5);
      g.lineStyle(1, UI.accent).strokeRect(x + w / 2 - 1.5, starY - 1.5, 3, 4);
    } else if (!done && isNext && h >= 34) {
      const tag = label(this, x + w / 2, starY, 'NEXT', 1, UI.accent).setOrigin(0.5, 0);
      // A little hop now and then: this is the one to play.
      if (!this.still) {
        this.tweens.add({
          targets: tag,
          y: starY - 1,
          duration: 1,
          hold: 380,
          yoyo: true,
          repeat: -1,
          repeatDelay: 900,
          delay: 600,
        });
      }
    } else if (!done && !open && h >= 34) {
      g.fillStyle(0x10180c).fillRect(x + w / 2 - 3, starY + 2, 7, 5);
      g.lineStyle(1, 0x10180c).strokeRect(x + w / 2 - 1.5, starY - 0.5, 3, 3);
    }

    if (open) {
      const zone = this.add
        .zone(x, y, w, h)
        .setOrigin(0)
        .setData('label', num) // e2e: press(page, '7') selects lawn 7
        .setInteractive({ useHandCursor: true });
      zone.on('pointerup', (p?: Phaser.Input.Pointer) => {
        if (p && p.getDistance() > 8) return; // a swipe across the grid, not a tap
        this.registry.set(REG_SELECTED, n);
        // Point at MOW! every time, even for the lawn that was already selected: that's how to start.
        this.nudgeMow = true;
        this.redraw();
      });
    }
  }

  /** The selected lawn: mini map, name, stars, mower chip and MOW!, in thumb reach. */
  private drawLawnCard(top: number, n: number, next: number): void {
    const { width } = this.scale.gameSize;
    const save = saves().save;
    const level = CAMPAIGN_LEVELS[n - 1]!;
    const stars = save.levels[level.id]?.stars ?? 0;
    const done = (stars & STAR.clear) !== 0;
    const open = isCampaignLevelOpen(save, n);
    const g = this.add.graphics();
    navyPanel(g, 4, top, width - 8, CARD_H);

    // Mini map of the real lawn (tap it for the full level card).
    const px = thumbnailScale(level, 44, CARD_H - 12);
    // (Centered in its slot: the storm boss's long, narrow lawn is much thinner than the slot.)
    const thumb = this.add
      .image(10 + 22, top + CARD_H / 2, lawnThumbnail(this, level, px, done), undefined)
      .setOrigin(0.5, 0.5);
    thumb.setX(Math.round(10 + 22 - thumb.width / 2) + thumb.width / 2);
    g.lineStyle(1, UI.shadow).strokeRect(
      thumb.x - thumb.width / 2 - 0.5,
      thumb.y - thumb.height / 2 - 0.5,
      thumb.width + 1,
      thumb.height + 1,
    );
    const tx = 10 + 44 + 8;
    if (open) {
      // The card's left side (map, name, stars, mower) opens the full level card: its star goals
      // and the mower picker. An INFO tab on the map says so (owner, 2026-10-06: it wasn't clear).
      this.add
        .zone(4, top, width - 4 - 6 - 72 - 4 - 4, CARD_H)
        .setOrigin(0)
        .setInteractive({ useHandCursor: true })
        .on('pointerup', () => this.openLevelCard(n));
      const tabW = 30;
      bevelButton(
        this,
        Math.round(thumb.x - tabW / 2),
        Math.round(thumb.y + thumb.height / 2) - 7,
        tabW,
        11,
        'INFO',
        () => this.openLevelCard(n),
      );
    }

    // The number goes on the heading, so the name keeps to its one line (campaign.test).
    label(this, tx, top + 6, `${n === next && !done ? 'NEXT: ' : ''}LAWN ${n}`, 1, UI.dim);
    label(this, tx, top + 17, level.name, 1, UI.accent);
    STAR_BITS.forEach((bit, k) => drawBigStar(g, tx + k * 9, top + 30, (stars & bit) !== 0));

    if (!open) {
      label(this, tx, top + 44, `Clear lawn ${n - 1} first`, 1, UI.dim);
      return;
    }
    // Mower chip: what you are bringing; tap for the full level card (goals, mower picker).
    const mower = this.mowerFor(level);
    const chipX = tx;
    const chipY = top + 40;
    g.fillStyle(UI.shadow).fillRect(chipX - 1, chipY - 1, 30, 20);
    g.fillStyle(UI.panelHi).fillRect(chipX, chipY, 28, 18);
    if (mower) {
      const img = this.add.image(chipX + 14, chipY + 9, ownMowerTexture(this, mower, save), Dir.E);
      img.setScale(Math.min(0.75, 26 / img.width));
      centerMowerIcon(img, mower);
    }
    // A gold corner: the mower can be changed (on the level card).
    const corner = this.add.graphics();
    corner.fillStyle(UI.accent);
    for (let k = 0; k < 4; k++) corner.fillRect(chipX + 27 - k, chipY + 14 + k, k + 1, 1);
    // Past the free lawns without the full game: the offer instead of MOW!.
    const paid = needsFullGame(save, n);
    const mow = bevelButton(
      this,
      width - 4 - 6 - 72,
      top + CARD_H - 30,
      72,
      24,
      paid ? 'UNLOCK' : 'MOW!',
      () => (paid ? this.openUnlock() : this.mow(n)),
      true,
      paid ? 1 : 2,
    );
    if (this.nudgeMow) {
      this.nudgeMow = false;
      callAttention(this, mow, 72, 24, save.settings.reducedShake);
    }
  }

  /** The mower MOW! uses: the last pick if this lawn allows it, else the first allowed owned one. */
  private mowerFor(level: LevelDef): MowerId | null {
    const save = saves().save;
    const usable = allowedMowers(level.mowerRule).filter((id) => save.ownedMowers.includes(id));
    const remembered = this.registry.get(REG_MOWER_CHOICE) as MowerId | undefined;
    return (
      [remembered, save.selectedMower].find((id): id is MowerId => !!id && usable.includes(id)) ??
      usable[0] ??
      null
    );
  }

  private campaignPlaylist(index: number): Playlist {
    return { kind: 'campaign', short: 'Lawn', levels: CAMPAIGN_LEVELS, index };
  }

  private openLevelCard(n: number): void {
    const level = CAMPAIGN_LEVELS[n - 1]!;
    this.openCard({
      level,
      choices: allowedMowers(level.mowerRule),
      playlist: this.campaignPlaylist(n - 1),
    });
  }

  /** The full game's offer, back to the menu after. */
  private openUnlock(): void {
    wipeTo(this, 'Unlock', { backTo: 'Menu' });
  }

  /** Starts the lawn straight away with the remembered mower (one tap to play). */
  private mow(n: number): void {
    if (needsFullGame(saves().save, n)) {
      this.openUnlock();
      return;
    }
    const level = CAMPAIGN_LEVELS[n - 1]!;
    const mowerId = this.mowerFor(level);
    if (!mowerId) {
      this.openLevelCard(n); // no usable mower: the card explains why
      return;
    }
    saves().update((s) => (s.selectedMower = mowerId));
    const data: LevelSceneData = { level, mowerId, playlist: this.campaignPlaylist(n - 1) };
    wipeTo(this, 'Level', data);
  }

  private openCard(data: LevelCardData): void {
    this.scene.start('LevelCard', data);
  }

  // ── Daily / Endless / Dev ─────────────────────────────────────────────────────────────────

  /**
   * The Daily (lm-y07): today's lawn as a card with a calendar tile, a status you can read at a
   * glance (to do, with the coins it pays; or done, with your best time), the streak with the
   * last week as dots, when the next lawn comes, the loaner mower, and PLAY.
   */
  private drawDaily(top: number, bottom: number): void {
    const { width } = this.scale.gameSize;
    const now = new Date();
    const key = todayKey(now);
    this.dailyShown = key;
    let daily = this.registry.get(REG_DAILY) as DailyLawn | undefined;
    if (!daily || daily.dateKey !== key) {
      daily = dailyLawn(key);
      this.registry.set(REG_DAILY, daily);
    }
    const d = daily;
    const save = saves().save;
    const best = dailyBestTicks(key);
    const done = best !== null;
    // The streak as it stands: alive if the last clear was today or yesterday.
    const dayKey = (back: number) =>
      todayKey(new Date(now.getFullYear(), now.getMonth(), now.getDate() - back));
    const { lastDate, count } = save.daily.streak;
    const streak = lastDate === key || lastDate === dayKey(1) ? count : 0;
    const nextStreak = lastDate === key ? count : lastDate === dayKey(1) ? count + 1 : 1;
    const g = this.add.graphics();

    // The card: a calendar tile, then the lawn's name.
    const cardY = top;
    const cardH = 56;
    navyPanel(g, 4, cardY, width - 8, cardH);
    const calX = 12;
    const calY = cardY + 8;
    g.fillStyle(UI.shadow).fillRect(calX - 1, calY - 1, 38, 42);
    g.fillStyle(0xf4f1de).fillRect(calX, calY, 36, 40);
    g.fillStyle(0xd8432f).fillRect(calX, calY, 36, 11);
    const month = now.toLocaleString('en-US', { month: 'short' }).toUpperCase();
    label(this, calX + 18, calY + 2, month, 1, 0xffffff).setOrigin(0.5, 0);
    label(this, calX + 18, calY + 15, String(now.getDate()), 3, UI.shadow).setOrigin(0.5, 0);
    const tx = calX + 46;
    label(this, tx, cardY + 9, "TODAY'S LAWN", 1, UI.dim);
    label(this, tx, cardY + 20, d.def.name.replace('Daily: ', ''), 1, UI.accent).setMaxWidth(
      width - tx - 10,
    );
    label(this, tx, cardY + 40, 'Same lawn for all', 1, UI.dim);

    // Status: done (with your best) or to do (with what it pays).
    const statusY = cardY + cardH + 6;
    const statusH = 22;
    g.fillStyle(UI.shadow).fillRect(5, statusY + 1, width - 10, statusH);
    g.fillStyle(done ? 0x2f7a3a : 0x3a2f12).fillRect(4, statusY, width - 10, statusH);
    g.lineStyle(1, done ? UI.good : UI.accent).strokeRect(
      4.5,
      statusY + 0.5,
      width - 11,
      statusH - 1,
    );
    // Today's leaderboard (online scores on).
    if (onlineOn()) {
      bevelButton(this, width - 56, statusY + 4, 48, 14, 'BOARD', () =>
        this.scene.start('Boards', { open: 'daily' } satisfies BoardsData),
      );
    }
    if (done) {
      drawCheck(g, 12, statusY + 7, true);
      label(this, 26, statusY + 4, 'DONE TODAY', 1, 0xeaffd8);
      label(this, 26, statusY + 13, `Best ${formatTime(best, TICK_RATE)}`, 1, UI.text);
    } else {
      const coins = dailyReward(true, nextStreak);
      label(this, 12, statusY + 4, 'NOT PLAYED YET', 1, UI.accent);
      const pays = label(this, 12, statusY + 13, `Clear it for ${coins}`, 1, UI.text);
      drawCoin(g, Math.round(pays.x + pays.width + 3), pays.y);
      const pulse = this.add.graphics();
      pulse.lineStyle(1, UI.accent).strokeRect(2.5, statusY - 1.5, width - 7, statusH + 3);
      if (!this.still) {
        this.tweens.add({ targets: pulse, alpha: { from: 0.9, to: 0 }, duration: 900, repeat: -1 });
      } else pulse.setAlpha(0.6);
    }

    // The loaner mower, just above the button (placed first: its picture's height sets the room
    // left for the lawn).
    const playTop = bottom - 34;
    const ly = playTop - 28;
    const tag = label(this, 8, ly + 8, 'LOANER', 1, UI.dim);
    // The icon after the tag, the name after the icon (the Fairway Mower is three tiles wide).
    const icon = this.add.image(0, ly + 11, ownMowerTexture(this, d.loaner, save), Dir.E);
    const iconX = tag.x + tag.width + 4;
    icon.setX(iconX + Math.round(icon.width / 2));
    centerMowerIcon(icon, d.loaner);
    label(this, iconX + icon.width + 4, ly + 8, MOWERS[d.loaner].name, 1, UI.text);
    const loanerTop = Math.min(ly, Math.floor(icon.getBounds().top));

    // The lawn itself, in the room left above the streak (short screens such as an iPad's go
    // without it: the card shows it before play).
    let thumbTop = statusY + statusH + 8;
    // The week's featured lawn (online, once the server has said which): its board, where it is
    // played.
    const featured = onlineOn() ? featuredNow() : null;
    if (featured) {
      bevelButton(this, 6, thumbTop - 2, width - 12, 16, `WEEKLY: LAWN ${featured.lawn}`, () =>
        this.scene.start('Boards', { open: 'featured' } satisfies BoardsData),
      );
      thumbTop += 20;
    }
    const streakH = 22;
    const thumbRoom = loanerTop - 6 - streakH - 8 - thumbTop;
    let sy = thumbTop;
    if (thumbRoom >= 32) {
      const px = thumbnailScale(d.def, width - 40, Math.min(110, thumbRoom));
      const thumb = this.add
        .image(width / 2, thumbTop, lawnThumbnail(this, d.def, px, done))
        .setOrigin(0.5, 0);
      g.lineStyle(1, 0xffffff).strokeRect(
        Math.round(thumb.x - thumb.width / 2) - 1.5,
        thumb.y - 1.5,
        thumb.width + 3,
        thumb.height + 3,
      );
      sy = thumbTop + thumb.height + 8;
    }

    // Streak: the count and the last seven days (today on the right).
    label(this, 8, sy, `STREAK ${streak}`, 1, streak > 0 ? UI.accent : UI.dim);
    for (let k = 0; k < 7; k++) {
      const back = 6 - k;
      const cleared = save.daily.bestTicks[dayKey(back)] !== undefined;
      const cx = width - 8 - 7 * 11 + k * 11 + 4;
      g.fillStyle(UI.shadow).fillCircle(cx, sy + 3, 4);
      g.fillStyle(cleared ? UI.good : 0x3a3366).fillCircle(cx, sy + 3, 3);
      if (back === 0) g.lineStyle(1, UI.accent).strokeCircle(cx, sy + 3, 5);
    }
    // When the next lawn comes (local midnight), kept up to date.
    const next = label(this, 8, sy + 12, '', 1, UI.dim);
    const tick = () => {
      const t = new Date();
      // Past midnight with the tab up: today's lawn.
      if (todayKey(t) !== key) return this.redraw();
      const midnight = new Date(t.getFullYear(), t.getMonth(), t.getDate() + 1);
      const mins = Math.max(0, Math.ceil((midnight.getTime() - t.getTime()) / 60000));
      next.setText(`New lawn in ${Math.floor(mins / 60)}h ${mins % 60}m`);
    };
    tick();
    this.time.addEvent({ delay: 20_000, loop: true, callback: tick });

    // Primary action at the bottom, in thumb reach.
    bevelButton(
      this,
      6,
      playTop,
      width - 12,
      26,
      done ? 'BEAT YOUR TIME' : 'PLAY',
      () =>
        this.openCard({
          level: d.def,
          choices: [d.loaner],
          freePick: true,
          mowerHeading: 'LOANER MOWER',
          daily: d.dateKey,
        }),
      true,
      2,
    );
  }

  /**
   * Endless: pick a mode. Endless Lawns is open from the start; each boss's run opens once that
   * boss is beaten in the campaign. Each card shows the mode's best score.
   */
  private drawEndless(top: number, bottom: number): void {
    const { width } = this.scale.gameSize;
    const save = saves().save;
    label(this, width / 2, top, 'PICK A MODE', 1, UI.dim).setOrigin(0.5, 0);
    // (One card more under the modes: the lawn builder.)
    const extra = 1;
    const cardH = Math.min(
      40,
      Math.floor((bottom - top - 16) / (ENDLESS_MODES.length + extra)) - 4,
    );
    // Short screens: one line per card (name and score, or the lawn that opens it).
    const roomy = cardH >= 24;
    ENDLESS_MODES.forEach((m, i) => {
      const mark = this.children.length;
      const y = top + 12 + i * (cardH + 4);
      const bossLevel = m.boss
        ? Number(Object.entries(BOSS_LEVELS).find(([, k]) => k === m.boss)?.[0])
        : null;
      const open =
        bossLevel === null ||
        ((save.levels[campaignLevelId(bossLevel)]?.stars ?? 0) & STAR.clear) !== 0;
      const g = this.add.graphics();
      g.fillStyle(UI.shadow).fillRect(5, y + 1, width - 10, cardH);
      g.fillStyle(open ? UI.panel : 0x1a1538).fillRect(4, y, width - 10, cardH);
      g.lineStyle(1, open ? UI.panelHi : 0x2a2550).strokeRect(4.5, y + 0.5, width - 11, cardH - 1);
      // Icon: the boss's badge, or a little lawn for Endless Lawns.
      const icon = this.add.graphics();
      if (m.boss) drawBossBadge(icon, m.boss, 10, y + Math.round(cardH / 2) - 5);
      else drawWorldIcon(icon, 'backyard', 10, y + Math.round(cardH / 2) - 5);
      if (!open) icon.setAlpha(0.35);
      // (Snug cards lift both lines, so descenders clear the frame.)
      const snug = cardH < 27;
      const nameY = roomy ? y + (snug ? 4 : 5) : y + Math.round((cardH - 7) / 2);
      label(this, 26, nameY, m.name.toUpperCase(), 1, open ? UI.accent : UI.dim);
      if (roomy) {
        label(
          this,
          26,
          y + (snug ? 13 : 15),
          open ? m.blurb : `Beat lawn ${bossLevel} to unlock`,
          1,
          UI.dim,
        );
      }
      if (!open) {
        // Locked: a padlock where an open mode shows NEW or its best (with the lawn that opens
        // it beside it on a one-line card).
        const lockX = roomy
          ? width - 18
          : Math.round(
              label(this, width - 10, nameY, `LAWN ${bossLevel}`, 1, UI.dim).setOrigin(1, 0).x -
                6 * `LAWN ${bossLevel}`.length -
                12,
            );
        drawPadlock(this.add.graphics(), lockX, nameY - 2);
      }
      if (open) {
        const best = endlessBest(m.id);
        label(this, width - 10, nameY, best > 0 ? `BEST ${best}` : 'NEW', 1, UI.text).setOrigin(
          1,
          0,
        );
        this.add
          .zone(4, y, width - 10, cardH)
          .setOrigin(0)
          .setData('label', m.name)
          .setInteractive({ useHandCursor: true })
          .on('pointerup', () => this.startEndless(m.id));
      }
      if (this.entrance === 'enter') arriveSince(this, mark, 0, 8, 40 + i * 50, 240);
    });
    this.drawBuilderCard(top + 12 + ENDLESS_MODES.length * (cardH + 4), cardH, roomy);
  }

  /**
   * The lawn builder's card (economy pass 2): locked until lawn 40 (a padlock and the lawn), then
   * its price until bought, then how many lawns you have made. Opens My Lawns.
   */
  private drawBuilderCard(y: number, cardH: number, roomy: boolean): void {
    const { width } = this.scale.gameSize;
    const save = saves().save;
    const open = builderOpen(save);
    const mark = this.children.length;
    const g = this.add.graphics();
    g.fillStyle(UI.shadow).fillRect(5, y + 1, width - 10, cardH);
    g.fillStyle(open ? UI.panel : 0x1a1538).fillRect(4, y, width - 10, cardH);
    g.lineStyle(1, open ? UI.panelHi : 0x2a2550).strokeRect(4.5, y + 0.5, width - 11, cardH - 1);
    const icon = this.add.graphics();
    drawWorldIcon(icon, 'park', 10, y + Math.round(cardH / 2) - 5);
    if (!open) icon.setAlpha(0.35);
    const snug = cardH < 27;
    const nameY = roomy ? y + (snug ? 4 : 5) : y + Math.round((cardH - 7) / 2);
    label(this, 26, nameY, 'LAWN BUILDER', 1, open ? UI.accent : UI.dim);
    if (roomy) {
      label(
        this,
        26,
        y + (snug ? 13 : 15),
        open ? 'Make your own lawns.' : `Beat lawn ${BUILDER_SHOP.opensAfter} to unlock`,
        1,
        UI.dim,
      );
    }
    // (Even locked it opens My Lawns: a friend's lawn can be played without the builder.)
    this.add
      .zone(4, y, width - 10, cardH)
      .setOrigin(0)
      .setData('label', 'LAWN BUILDER')
      .setInteractive({ useHandCursor: true })
      .on('pointerup', () => wipeTo(this, 'MyLawns', {}));
    if (!open) {
      drawPadlock(this.add.graphics(), width - 18, nameY - 2);
    } else {
      const price = builderPrice(save);
      const n = save.customLawns.length;
      const right =
        price !== null ? String(price) : n > 0 ? `${n} LAWN${n === 1 ? '' : 'S'}` : 'NEW';
      const t = label(
        this,
        width - 10,
        nameY,
        right,
        1,
        price !== null ? UI.accent : UI.text,
      ).setOrigin(1, 0);
      if (price !== null) drawCoin(this.add.graphics(), Math.round(t.x - t.width - 10), nameY);
    }
    if (this.entrance === 'enter')
      arriveSince(this, mark, 0, 8, 40 + ENDLESS_MODES.length * 50, 240);
  }

  /** Opens the card for a new run in an endless mode (a mower pick, then MOW!). */
  private startEndless(mode: EndlessMode): void {
    const save = saves().save;
    const runSeed = newRunSeed();
    // Boss runs: the Fairway Mower's side reels sit bosses out.
    const choices = ownedMowerIds(save).filter((id) => endlessModeAllows(mode, id));
    const mower = choices.includes(save.selectedMower) ? save.selectedMower : choices[0];
    if (!mower) return;
    this.openCard({
      level: mode === 'lawns' ? endlessLawn(runSeed, 1) : endlessStage(mode, runSeed, 1, mower),
      choices,
      endless: {
        mode,
        runSeed,
        ...(mode !== 'lawns' ? { firstLawnFor: mower } : {}),
        stage: 1,
        score: 0,
      },
    });
  }

  private drawTests(top: number, bottom: number): void {
    const levels: readonly LevelDef[] = PROTO_LEVELS;
    const zones = BIOMES.filter((b) => b.levels[1] <= CAMPAIGN_LEVELS.length);
    // Rows close up on short screens so the whole list fits above the nav.
    const need = (levels.length + 1) * (ROW - 1) + 6 + (zones.length + 2) * ROW + 9;
    const k = Math.min(1, (bottom - 4 - top) / need);
    const step = Math.max(9, Math.floor((ROW - 1) * k));
    const zoneStep = Math.max(9, Math.floor(ROW * k));
    const open = (list: readonly LevelDef[], i: number) => {
      const level = list[i]!;
      this.openCard({
        level,
        choices: allowedMowers(level.mowerRule).filter((id) => MOWER_IDS.includes(id)),
        freePick: true,
        playlist: { kind: 'test', short: 'Test', levels: list, index: i },
      });
    };
    let y = top;
    // The lawn builder (in the dev area until its coin unlock, lm-ma4.16).
    button(this, 6, y, 'LAWN BUILDER', () => wipeTo(this, 'MyLawns', {}), 1, UI.accent);
    y += step;
    levels.forEach((level, i) => {
      button(this, 6, y, `${i + 1}. ${level.name}`, () => open(levels, i));
      y += step;
    });

    // Every zone: a lawn that shows off what it brings, and its boss (one playlist, in order).
    y += 6;
    label(this, 6, y, 'ZONES', 1, UI.dim);
    y += zoneStep;
    // And the biggest regular lawn (not a boss), the latest of any tie: the widest maps.
    const biggest = CAMPAIGN_LEVELS.filter((l) => !bossFor(l.level ?? 0)).reduce((a, b) =>
      b.sim.width * b.sim.height >= a.sim.width * a.sim.height ? b : a,
    );
    const zoneLevels = [
      ...zones.flatMap((b) => [ZONE_SAMPLE[b.id], b.levels[1]].map((n) => CAMPAIGN_LEVELS[n - 1]!)),
      biggest,
    ];
    const g = this.add.graphics();
    zones.forEach((b, z) => {
      drawWorldIcon(g, b.id, 6, y - 1);
      button(this, 20, y, `LAWN ${ZONE_SAMPLE[b.id]}`, () => open(zoneLevels, z * 2));
      const boss = bossFor(b.levels[1]);
      if (boss) drawBossBadge(g, boss, 86, y - 1);
      button(this, 99, y, `BOSS ${b.levels[1]}`, () => open(zoneLevels, z * 2 + 1));
      y += zoneStep;
    });
    button(
      this,
      6,
      y,
      `BIGGEST: LAWN ${biggest.level} (${biggest.sim.width}x${biggest.sim.height})`,
      () => open(zoneLevels, zoneLevels.length - 1),
    );
    y += zoneStep;
    if (y + 9 <= bottom) label(this, 6, y + 2, 'Any mower, no rewards.', 1, UI.dim);
  }
}

/**
 * DEV tab: the lawn shown for each zone, one that brings out its new things (the Park's
 * sprinklers, the Farm's dog, the Golf Course's moles, the Cemetery's thorns and
 * falling trees, the Estate's river and hills, the Stadium's stray balls).
 */
const ZONE_SAMPLE: Readonly<Record<BiomeId, number>> = {
  backyard: 16,
  park: 31,
  farm: 47,
  golf: 67,
  cemetery: 91,
  estate: 113,
  stadium: 131,
  meadow: 153,
};
